<template lang="pug">
#other-user-group-member-register(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 施設関係者メールユーザー情報登録

    .box-body.register-body
      FormInputSelect(
        key-name="種類・グループ"
        required
        name="group"
        v-model="groupId"
        :options="groupOptions"
        :validation-rules="{ required: true }"
        :styles="{ 'max-width': '540px' }"
      )
      FormInput(
        key-name="氏名"
        required
        name="name"
        v-model="name"
        type="text"
        placeholder="氏名"
        :maxlength="32"
        :validation-rules="{ required: true, max: 32 }"
        :styles="{ width: '340px' }")
      FormInput(
        key-name="所属／係／担当"
        name="organization"
        v-model="organization"
        type="text"
        placeholder="所属／係／担当"
        :maxlength="32"
        :validation-rules="{ max: 32 }"
        :styles="{ width: '340px' }")
      FormInput(
        key-name="電話番号"
        required
        name="tel"
        v-model="tel"
        type="text"
        placeholder="電話番号"
        option-text="-（ハイフン）なしで入力してください。"
        :maxlength="11"
        :validation-rules="{ required: true, numeric: true, max: 13, min: 5 }"
        :styles="{ width: '340px' }")
      FormInput(
        key-name="メールアドレス"
        required
        name="email"
        v-model="email"
        type="text"
        placeholder="メールアドレス"
        :maxlength="100"
        :validation-rules="{ required: true, max: 100, email: true, equal_value: confirmEmail }"
        :styles="{ width: '540px' }")
      FormInput(
        key-name="メールアドレス（確認）"
        required
        name="confirm-email"
        v-model="confirmEmail"
        type="text"
        placeholder="メールアドレス（確認）"
        :maxlength="100"
        :validation-rules="{ required: true, max: 100, email: true }"
        :styles="{ width: '540px' }")
      .register-row.caution-message
        .register-group.font-strong-color メールアドレスが間違っていないか、登録後に登録確認メッセージを送信し、受信できているかをお電話等でご確認ください。

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.register-btn(@click="showConfirmModal" type="button") 登録
        button.btn.cancel-btn(@click="gotoOtherUserGroupList" type="button") キャンセル

  OkCancelModal(v-model="showRegisterModal" title="施設関係者ユーザー情報を登録しますか？" @ok="registerOtherUserGroupMember")
  OkModal(v-model="showRegisterSuccessModal" title="登録に成功しました。" @ok="gotoOtherUserGroupList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { OtherUserGroupListState, SendMessagesState } from "@/store/NeedRefreshes";

import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import OtherUserGroupsService from "@/commons/services/OtherUserGroups";
import OtherUserGroupModel from "@/commons/models/OtherUserGroup";
import OtherUserGroupTypesService from "@/commons/services/OtherUserGroupTypes";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";
import MailOtherUserGroupMembersService, { MailOtherUserGroupMembersPostModel } from "@/commons/services/MailOtherUserGroupMembers";
import SystemError from "@/commons/models/SystemError";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";

/**
 * 施設関係者グループメールメンバー登録画面コンポーネント
 */
@Component({
  components: {
    FormInputSelect,
    FormInput,
    PageLoading,
    ValidationAlert,
    OkCancelModal,
    OkModal
  },
  $_veeValidate: { validator: "new" }
})
export default class OtherUserGroupMemberRegister extends Vue {
  /** 施設関係者グループサービス */
  @Inject("OtherUserGroupsService") otherUserGroupsService!: OtherUserGroupsService;
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;
  /** 施設関係者グループメールメンバーサービス */
  @Inject("MailOtherUserGroupMembersService") mailOtherUserGroupMembersService!: MailOtherUserGroupMembersService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 登録確認モーダル表示可否 */
  showRegisterModal: boolean = false;
  /** 登録完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 施設関係者グループID */
  groupId: any = "";
  /** 施設関係者グループセレクトボックス用項目 */
  groupOptions: Array<{ value: number | string; label: string }> = [];
  /** 施設関係者グループメンバー名 */
  name: string = "";
  /** 所属／係／担当 */
  organization: string = "";
  /** 電話番号 */
  tel: string = "";
  /** メールアドレス */
  email: string = "";
  /** メールアドレス(確認) */
  confirmEmail: string = "";

  /** APIから返却された場合に表示するエラーメッセージ */
  errorMessage: string = "";

  /**
   * vue lifecycle created
   */
  public created(): void {
    CustomValidate.addCustomValidate(this.$validator);

    return;
  }

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // セレクトボックスで使用する施設関係者グループリストを作成する
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.getOtherGroupTypes();
    const otherUserGroupTypesMap: Map<number, string> = this.createOtherUserGroupTypeMap(otherUserGroupTypes);
    const otherUserGroups: Array<OtherUserGroupModel> = await this.getOtherUserGroups();
    const groupOptions: Array<{ value: number | string; label: string }> = this.createGroupOptions(otherUserGroups, otherUserGroupTypesMap);
    this.groupOptions = groupOptions;

    this.isBusy = false;

    return;
  }

  /**
   * ### 施設関係者グループ種類を取得する
   */
  public async getOtherGroupTypes(): Promise<Array<OtherUserGroupTypeModel>> {
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);

    return otherUserGroupTypes;
  }

  /**
   * ### 画面表示用に施設関係者グループ種類のMAPを作成する
   */
  public createOtherUserGroupTypeMap(otherUserGroupTypes: Array<OtherUserGroupTypeModel>): Map<number, string> {
    let map: Map<number, string> = new Map<number, string>();

    for (const type of otherUserGroupTypes) {
      map.set(type.id, type.name);
    }

    return map;
  }

  /**
   * ### 施設関係者グループを取得する
   */
  public async getOtherUserGroups(): Promise<Array<OtherUserGroupModel>> {
    let response: Array<OtherUserGroupModel> = [];

    try {
      response = await this.otherUserGroupsService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.otherUserGroupsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * 300,
            endIndex: i * 300 + 300
          };
          response = response.concat(await this.otherUserGroupsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 施設関係者グループセレクトボックス用のoptionを作成する
   *
   * - value値は施設関係者グループID
   * - label値は「{種類名}・{グループ名}」の形式
   */
  public createGroupOptions(otherUserGroups: Array<OtherUserGroupModel>, otherUserGroupTypesMap: Map<number, string>): Array<{ value: number | string; label: string }> {
    let options: Array<{ value: number | string; label: string }> = [];

    for (const otherUserGroup of otherUserGroups) {
      const typeName: string | undefined = otherUserGroupTypesMap.get(otherUserGroup.groupTypeId);
      const labelTypeName: string = typeName ? typeName : "";

      const option: { value: number | string; label: string } = {
        value: otherUserGroup.id,
        label: `${labelTypeName}・${otherUserGroup.name}`
      };
      options.push(option);
    }

    return options;
  }

  /**
   * ### 確認モーダルを表示する
   *
   * - バリデーターを使用してバリデーションする
   * - バリデーションでエラーが無ければ確認モーダルを表示する
   */
  public async showConfirmModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showRegisterModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ### 施設関係者グループ一覧画面へ遷移する
   */
  public gotoOtherUserGroupList(): void {
    this.$router.push(PcUrl.OtherUserGroupList);

    return;
  }

  /**
   * ### 施設関係者グループメールメンバーを登録する
   */
  public async registerOtherUserGroupMember(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    try {
      const requestModel: MailOtherUserGroupMembersPostModel = this.createRequestModel();
      await this.mailOtherUserGroupMembersService.post(userStore, this.groupId, requestModel);

      // 施設関係者を再読み込みさせるようstoreを更新
      let otherUserGroupList: OtherUserGroupListState = needRefreshesStore.otherUserGroupList;
      otherUserGroupList.otherUserGroupList = true;
      needRefreshesStore.setOtherUserGroupList(otherUserGroupList);
      // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
      let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
      sendMessageState.sendMessages = true;
      needRefreshesStore.setSendMessage(sendMessageState);

      // 登録成功ダイアログを表示する
      this.showRegisterSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        this.errorMessage = "既に同一のメールアドレスが登録されているため登録できません。";
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 施設関係者グループメールメンバー登録用のパラメータを作成する
   *
   * - このメソッドが呼び出される際は既に値のバリデーションが済んでいるものとする
   */
  public createRequestModel(): MailOtherUserGroupMembersPostModel {
    const requestModel: MailOtherUserGroupMembersPostModel = {
      memberName: this.name,
      memberOrganization: this.organization ? this.$stringUtil.convertYen(this.organization) : "入力無し",
      memberMailaddress: this.email,
      memberTel: this.tel
    };

    return requestModel;
  }
}
</script>

<style lang="scss" scoped>
// 親要素からネストしてスタイルを指定する
#other-user-group-member-register {
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
